import { type OptimisticOrderTarget, targetKey } from '@/state/order'
import { Instrument } from '@/types/generated/common/types'
import {
  LimitPriceAnchor,
  LimitPriceOffsetUnit,
  RelativeLimitPrice,
} from '@/types/generated/execution'

const PRICE_ANCHOR_MAP: Record<string, LimitPriceAnchor> = {
  mid: LimitPriceAnchor.LIMIT_PRICE_ANCHOR_MID_PRICE,
  best_bid: LimitPriceAnchor.LIMIT_PRICE_ANCHOR_BEST_BID,
  best_ask: LimitPriceAnchor.LIMIT_PRICE_ANCHOR_BEST_ASK,
}

const PRICE_OFFSET_UNIT_MAP: Record<string, LimitPriceOffsetUnit> = {
  quote_value: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_QUOTE_VALUE,
  bps: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_BPS,
  percent: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_PERCENT,
  ticks: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_TICKS,
}

// A relative limit price (anchor + offset). Shared by the un-triggered simple
// submit and the simple-mode trigger fire payload so they never diverge.
export function buildRelativePrice(
  priceAnchor: string,
  priceOffsetUnit: string,
  offset: string
): RelativeLimitPrice {
  return {
    anchor: PRICE_ANCHOR_MAP[priceAnchor] ?? LimitPriceAnchor.LIMIT_PRICE_ANCHOR_MID_PRICE,
    offset,
    offsetUnit:
      PRICE_OFFSET_UNIT_MAP[priceOffsetUnit] ??
      LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_QUOTE_VALUE,
  }
}

// Batch submissions can span multiple accounts/exchanges per row — collapse to the
// distinct (accountId, symbol) pairs actually submitted, for triggerOptimistic's
// composite-keyed pending-order tracking (see state/order.ts's targetKey).
export function dedupeTargets<T extends { accountId: string; symbol: string }>(
  params: T[]
): OptimisticOrderTarget[] {
  const map = new Map<string, OptimisticOrderTarget>()
  for (const p of params) {
    map.set(targetKey(p.accountId, p.symbol), { accountId: p.accountId, symbol: p.symbol })
  }
  return Array.from(map.values())
}

// resolveBaseCurrency hint carrying only the row's own baseCurrency. Always passed
// (even when empty), so resolution never looks the symbol up in an instrument map
// and falls straight back to parsing the symbol when the row has no baseCurrency.
export function baseCurrencyHint(baseCurrency: string | undefined): Instrument {
  return Instrument.fromPartial({ baseCurrency })
}
